import React, { useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { copy_text_base_css } from './styles';
import { IconCheck, IconCopy } from '../icons/generated';
import { BaseText, BaseTextProps } from '../text';
import { semantic_colors } from '../../foundation';
import { Stack } from '../stack';
import { Tooltip, TooltipProps } from '../tooltip';

export interface CopyTextProps {
  /**
   * 복사할 텍스트 내용
   */
  text: string;
  textProps?: BaseTextProps;
  /**
   * 복사할 텍스트 내용을 대체할 노드
   * displayText가 있으면 text를 대체하여 보여지고, 실제 복사는 text 정보를 사용합니다.
   */
  displayText?: ReactNode;
  /**
   * 복사 완료 시 표시할 텍스트
   * @default '복사했어요.'
   */
  copiedText?: string;
  tooltipProps?: Omit<TooltipProps, 'children'>;
  /** @default false */
  disabled?: boolean;
  className?: string;
  /**
   * 복사 완료 시 호출되는 콜백
   */
  onCopy?: (text: string) => void;
  /**
   * 복사 실패 시 호출되는 콜백
   */
  onCopyFailed?: (error: Error) => void;
}

export const CopyText = React.forwardRef<HTMLButtonElement, CopyTextProps>(
  (
    {
      text,
      textProps,
      tooltipProps,
      copiedText = '복사했어요.',
      disabled = false,
      className,
      displayText,
      onCopy,
      onCopyFailed,
      ...props
    },
    ref,
  ) => {
    const [copied, setCopied] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState<boolean | undefined>(undefined);
    const timeoutRef = useRef<number | null>(null);
    const { color, ...restTextProps } = textProps ?? {};

    const handleCopy = useCallback(
      async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (disabled) return;
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTooltipOpen(true);
          onCopy?.(text);
          if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = window.setTimeout(() => {
            setTooltipOpen(false);
            setTimeout(() => {
              setCopied(false);
            }, 100);
          }, 1000);
        } catch (error) {
          onCopyFailed?.(error as Error);
        }
      },
      [disabled, text, onCopy, onCopyFailed],
    );

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    const renderToButton = () => (
      <button
        ref={ref}
        type='button'
        className={classNames('pds-copy-text', { copied }, className)}
        css={copy_text_base_css}
        onClick={handleCopy}
        disabled={disabled}
        aria-label={text}
        {...props}
      >
        <Stack direction='row' align='center' gap={2}>
          <BaseText
            typography='Body_12_Regular'
            color={disabled ? semantic_colors.content.disabled : color ?? semantic_colors.content.primary}
            {...restTextProps}
          >
            {displayText || text}
          </BaseText>
          {copied ? (
            <IconCheck color={semantic_colors.state.positive} size={16} />
          ) : (
            <IconCopy
              className='copy-icon'
              size={16}
              color={disabled ? semantic_colors.content.disabled : semantic_colors.content.tertiary}
            />
          )}
        </Stack>
      </button>
    );

    return disabled ? (
      renderToButton()
    ) : (
      <Tooltip
        content={copied ? copiedText : tooltipProps?.content ?? '복사'}
        color='neutral'
        placement='top'
        openerTriggerEvent={copied ? 'none' : 'hover'}
        closeDelay={0}
        {...tooltipProps}
        {...(copied && { open: tooltipOpen })}
      >
        {renderToButton()}
      </Tooltip>
    );
  },
);

CopyText.displayName = 'CopyText';
